import { cn } from '@invent/wl-ui-kit-next';
import { Badge, Button, Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { FlaskConical, RotateCcw, X } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { VeritantWordmark } from '@/components/veritant-wordmark';
import {
  JOURNEYS,
  UNENTITLED_ACCOUNT,
  journey as journeyOf,
  type JourneyCode,
  type ProviderMode
} from './fixtures';
import { reduce, startRun, type StepCode } from './flow';
import { FlowMap } from './flow-map';
import { GapsPanel, StaffQueuePanel } from './panels';
import { FlowScreen } from './screens';
import { ProvenanceBadge, StudyChrome, WireEntry } from './shared';
import { TestDataSheet } from './test-data';

/**
 * The onboarding design study: registration, the migrated-account first login, and where
 * either one lands — clickable, over invented data, talking to nothing.
 *
 * ## Why it exists
 *
 * survey-api's registration roadmap is ten delivery blocks, and most have shipped on the
 * server — the catalogue, the door, BrokerCheck, SEC ADV, mail, teams. The question
 * in front of the team is not "does the code work" but "is this the process we want", and
 * that question cannot be answered from a plan document: the branches are what matter, and a
 * document lists them where a screen makes you walk one. Where a screen has since been built,
 * its strip links to the real one; the product never links back.
 *
 * ## The two rules it follows
 *
 * **Where the server already decides something, this mirrors it** — the three steps, the
 * three lookup answers, the two refusal reasons, the retry-then-branch rule, the wording of
 * each refusal. **Where it does not, the screen says so**, through a provenance badge and a
 * wire-log entry naming what would have to answer it. Every screen here is therefore either
 * a picture of the product or a specification for it, and which one is never left to the
 * reader to guess — the strip above each screen says it in both modes (`MockStatusStrip`).
 *
 * ## Anonymous, on purpose
 *
 * Registration is an anonymous surface: its user has no account by definition, and a
 * reviewer of it is not assumed to hold a staff or admin one. The Team Portal study sits
 * behind the admin guard because its audience is staff; this one would be unreachable there —
 * no staff or admin account is seeded to sign in as (README, "Seeing the Team Portal").
 *
 * ## The chrome is off by default
 *
 * With the rails, the tabs, the provider switch and a note under every paragraph, the screen
 * being reviewed is the smallest thing on the page — and the first question asked of a
 * prototype is about the screen, not about the reasoning behind it. So the page opens as the
 * respondent's view alone, marked by one pill in the corner, and that pill is what reveals
 * everything else. The pill stays visible rather than being hidden behind a keystroke, and so
 * does the status strip above the screen: an unmarked mock-up of a sign-up screen is
 * something somebody will eventually mistake for the product.
 *
 * The URL carries the whole choice — `?journey=`, `?providers=`, `?view=`, `?chrome=`, and
 * `?from=account` for a registration made inside an account ("Add a survey") — so a reviewer
 * can send a colleague exactly the branch they are asking about, with or without the
 * furniture. Which account that is lives in the run, never in the URL: a pasted link signs in
 * as the study's one account with an empty catalogue.
 */

/** The two floating controls in the quiet mode share one shape; only the tone differs. */
const PILL =
  'flex items-center gap-1.5 rounded-full border bg-card/90 px-3 py-1 text-xs shadow-sm ' +
  'backdrop-blur';

type ViewCode = 'flow' | 'map' | 'staff' | 'gaps';

const VIEWS: { code: ViewCode; label: string }[] = [
  { code: 'flow', label: 'Walk the flow' },
  { code: 'map', label: 'Every branch' },
  { code: 'staff', label: 'What staff see' },
  { code: 'gaps', label: 'What has to be built' }
];

/**
 * The three steps a run can actually sit on, which is what makes a 409 possible: the stale-tab
 * button is offered on those and nowhere else, because a screen the server never saw cannot be
 * resumed onto the wrong step.
 */
const SERVER_STEPS: StepCode[] = ['anchor', 'confirm_identity', 'route', 'complete'];

/** The front door is the default: the flow starts where a respondent would start. */
function asJourney(value: Nullable<string>): JourneyCode {
  return JOURNEYS.find((entry) => entry.code === value)?.code ?? 'entry';
}

export function OnboardingPreviewPage() {
  const [params, setParams] = useSearchParams();
  const journeyCode = asJourney(params.get('journey'));
  /**
   * **Live by default**, which is the opposite of what the product does — and deliberate.
   *
   * Stubbed is today's truth for NIPR, and it makes its happy path unreachable: every FSP
   * lookup answers `error` whatever is typed, so a reviewer's first attempt there lands on "we
   * could not confirm that number" and reads as a broken prototype rather than as a statement
   * about the registry. BrokerCheck and SEC ADV answer alike in both positions, because the
   * product asks the real ones. The page is for reviewing the process, so it opens
   * on the process; "what it does today" is a question somebody asks on purpose, and the
   * switch is how they ask it.
   */
  const providerMode: ProviderMode = params.get('providers') === 'stubbed' ? 'stubbed' : 'live';
  const chrome = params.get('chrome') === 'on';
  /** 'door' — a registration reached through the chooser; 'chooser' — the step back to it;
   *  'account' — a registration inside an account, the chooser included. */
  const from = params.get('from');
  const signedIn = from === 'account';
  const atStep: StepCode | undefined =
    journeyCode === 'entry' && from === 'chooser' ? 'choose_subject' : undefined;
  // The three other views are commentary, so they only exist with the chrome open — a
  // `?view=map&chrome=off` link would otherwise land on a blank page.
  const view: ViewCode = chrome
    ? (VIEWS.find((entry) => entry.code === params.get('view'))?.code ?? 'flow')
    : 'flow';

  const [state, dispatch] = React.useReducer(
    reduce,
    { journeyCode, providerMode, atStep, signedIn },
    (initial) =>
      startRun(
        initial.journeyCode,
        initial.providerMode,
        initial.atStep,
        initial.signedIn ? { email: UNENTITLED_ACCOUNT.email } : null
      )
  );

  /**
   * The URL owns the choice, so a pasted link lands where it says. The run is restarted
   * rather than migrated: it carries the survey it was opened for, and the server would have
   * to be asked for a new one too.
   *
   * Every write below is a `replace`, so the browser's own Back leaves the study rather than
   * stepping through journeys — deliberate, because a reviewer clicking six screens would
   * otherwise need six Backs to get out, and the flow's own "← Back" is what walks the steps.
   */
  React.useEffect(() => {
    if (
      state.journey !== journeyCode ||
      state.providerMode !== providerMode ||
      state.signedIn !== signedIn
    ) {
      dispatch({ type: 'journey', journey: journeyCode, mode: providerMode, atStep, signedIn });
    }
  }, [
    journeyCode,
    providerMode,
    atStep,
    signedIn,
    state.journey,
    state.providerMode,
    state.signedIn
  ]);

  /** Several keys at once, because the chooser's handoff moves two of them. '' removes one. */
  const set = (...pairs: [key: string, value: string][]) => {
    const copy = new URLSearchParams(params);
    for (const [key, value] of pairs) {
      if (value === '') copy.delete(key);
      else copy.set(key, value);
    }
    setParams(copy, { replace: true });
  };

  /**
   * The chooser's handoff. It goes through the URL rather than through the reducer because
   * the URL owns which journey is running — a dispatch would be undone by the effect above
   * on its next pass.
   *
   * `from` says which chooser it was: the door's, or the one inside an account that "Add a
   * survey" opens — which the run then is too, and "Register the firm" after a redirect keeps.
   */
  const pickJourney = (next: JourneyCode, via: 'door' | 'account' = 'door') =>
    set(['journey', next], ['from', via]);

  /** "Add a survey", from the catalogue. The reducer moves first — it keeps the account the
   *  catalogue was signed in as — and the URL then agrees with the chooser it shows. */
  const addSurvey = () => {
    dispatch({ type: 'add-survey' });
    set(['journey', 'entry'], ['from', 'account']);
  };

  /**
   * A step back out of the first screen of a registration, which exists only when a chooser is
   * what put the registrant there — `?from=door` or `account`. Picking a journey from the rail is
   * a study shortcut, and a Back that led somewhere the reviewer has never been would be
   * inventing a screen rather than showing one.
   *
   * The reducer moves first, so the abandonment it logs is appended to this run's wire rather
   * than lost with it; the URL then agrees with the chooser it already shows, and the effect
   * above has nothing to restart.
   */
  const backToDoor =
    from === 'door' || signedIn
      ? () => {
          dispatch({ type: 'back-to-chooser' });
          set(['journey', 'entry'], ['from', signedIn ? 'account' : 'chooser']);
        }
      : null;

  const current = journeyOf(journeyCode);

  if (!chrome) {
    return (
      <StudyChrome open={false}>
        <div className="min-h-screen bg-[var(--color-off-white)]">
          {/* Still a landmark with the chrome off: the screen is the whole page here, and a
              page whose only content sits in an anonymous div has none. */}
          <main>
            <FlowScreen
              // The run id is the key so a journey switch REMOUNTS the screen. FA, FSP and
              // RIA all open on `anchor`, so React keeps one AnchorScreen mounted across the
              // change and its local `idType` and `value` stay on the journey before — which
              // sent FA's own CRD to the wire as an NPN and answered not_found.
              key={state.runId}
              state={state}
              dispatch={dispatch}
              onPickJourney={pickJourney}
              onAddSurvey={addSurvey}
              onBackToDoor={backToDoor}
            />
          </main>

          {/* Two controls, and the crib is one of them even here: the invented registry knows
              seven numbers, so a reviewer without the list is one typo away from concluding
              the prototype does not work. */}
          <div className="fixed top-3 right-3 z-50 flex items-center gap-2">
            {/* Shown only in the position that changes outcomes. A mode that silently makes
                a registry's lookups fail has to be visible from the screen it is failing on —
                but in the default position it is one more control saying nothing. */}
            {providerMode === 'stubbed' && (
              <button
                type="button"
                onClick={() => set(['providers', 'live'])}
                className={cn(PILL, 'border-[var(--color-magenta)] text-[var(--color-deep-magenta)]')}
              >
                NIPR stubbed — its lookups fail
              </button>
            )}

            <TestDataSheet
              providerMode={providerMode}
              className="rounded-full bg-card/90 text-xs shadow-sm backdrop-blur"
            />
            <button
              type="button"
              onClick={() => set(['chrome', 'on'])}
              className={cn(PILL, 'text-muted-foreground hover:text-[var(--color-royal-blue)]')}
            >
              <FlaskConical className="size-3" aria-hidden />
              Design study
            </button>
          </div>
        </div>
      </StudyChrome>
    );
  }

  return (
    <StudyChrome open>
      <div className="flex min-h-screen flex-col bg-[var(--color-off-white)]">
        <header className="flex flex-wrap items-center gap-3 bg-[var(--color-midnight-blue)] px-5 py-3 text-white">
          <VeritantWordmark tone="white" className="text-2xl" />
          <Badge variant="outline" className="gap-1 border-white/40 text-white">
            <FlaskConical className="size-3" aria-hidden /> Design study
          </Badge>
          <p className="text-sm text-white/70">
            Registration and first login. Invented data, nothing reaches survey-api.
          </p>

          <div className="ml-auto flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 rounded-md bg-white/10 p-1 text-xs">
                  {(['stubbed', 'live'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      // A toggle group, so which position is set has to be readable by
                      // something other than the colour it is drawn in.
                      aria-pressed={providerMode === mode}
                      onClick={() => set(['providers', mode])}
                      className={cn(
                        'rounded px-2 py-1',
                        providerMode === mode ? 'bg-white text-[var(--color-midnight-blue)]' : ''
                      )}
                    >
                      {mode === 'stubbed' ? 'Registries as today' : 'All registries live'}
                    </button>
                  ))}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                Today BrokerCheck and SEC ADV are real and NIPR is NotImplementedFactProvider:
                every FSP lookup answers error, and its confirmation prompt is unreachable.
                “All live” pretends block 9 landed; FA and RIA are the same in both.
              </TooltipContent>
            </Tooltip>

            <TestDataSheet
              providerMode={providerMode}
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
            />

            <Button
              size="sm"
              variant="ghost"
              onClick={() => set(['chrome', 'off'])}
              className="text-white hover:bg-white/10 hover:text-white"
            >
              <X className="size-3" aria-hidden /> Hide
            </Button>
          </div>
        </header>

        <nav className="flex flex-wrap gap-1 border-b bg-card px-5 py-2">
          {VIEWS.map((entry) => (
            <Button
              key={entry.code}
              size="sm"
              variant={view === entry.code ? 'default' : 'ghost'}
              onClick={() => set(['view', entry.code])}
            >
              {entry.label}
            </Button>
          ))}
        </nav>

        {view === 'flow' && (
          <div className="grid flex-1 items-start gap-0 lg:grid-cols-[17rem_minmax(0,1fr)_22rem]">
            <aside className="border-b p-4 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto lg:border-r lg:border-b-0">
              <p className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                Who is registering
              </p>
              <ul className="grid gap-1">
                {JOURNEYS.map((entry) => (
                  <li key={entry.code}>
                    <button
                      type="button"
                      // A study shortcut, so it clears how the last run was reached.
                      onClick={() => set(['journey', entry.code], ['from', ''])}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-sm',
                        entry.code === journeyCode
                          ? 'bg-[var(--color-slight-blue)]/70 text-[var(--color-royal-blue)]'
                          : 'hover:bg-[var(--color-slight-blue)]/30'
                      )}
                    >
                      <span className="block font-medium">{entry.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{entry.who}</span>
                      <ProvenanceBadge provenance={entry.provenance} className="mt-1.5" />
                    </button>
                  </li>
                ))}
              </ul>

              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{current.blurb}</p>
              {current.roadmap && (
                <p className="mt-2 text-xs font-medium text-[var(--color-deep-magenta)]">
                  {current.roadmap}
                </p>
              )}

              <dl className="mt-6 grid gap-1 border-t pt-4 text-xs">
                <RunFact label="step" value={state.step} />
                <RunFact label="signedIn" value={String(state.signedIn)} />
                <RunFact label="lookup" value={state.lookup ?? '—'} />
                <RunFact label="attempts" value={String(state.attempts)} />
                <RunFact
                  label="destination"
                  value={
                    state.destination.pathCode
                      ? `${state.destination.surveyCode} / ${state.destination.pathCode}`
                      : state.destination.surveyCode
                  }
                />
                <RunFact label="runId" value={state.runId.slice(0, 8)} />
              </dl>

              <div className="mt-4 grid gap-2">
                <Button size="sm" variant="outline" onClick={() => dispatch({ type: 'restart' })}>
                  <RotateCcw className="size-3" aria-hidden /> Start this journey again
                </Button>
                {SERVER_STEPS.includes(state.step) && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => dispatch({ type: 'stale-tab' })}>
                      Resume a stale tab (409)
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => dispatch({ type: 'swept' })}>
                      Come back a week later (409)
                    </Button>
                  </>
                )}
              </div>
            </aside>

            <main className="min-w-0">
              <FlowScreen
                // See the other call site: remount per run, or a journey switch leaves the
                // previous journey's anchor type in a field the screen may not even show.
                key={state.runId}
                state={state}
                dispatch={dispatch}
                onPickJourney={pickJourney}
                onAddSurvey={addSurvey}
                onBackToDoor={backToDoor}
              />
            </main>

            <aside className="border-t p-4 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto lg:border-t-0 lg:border-l">
              <p className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                What went over the wire
              </p>
              {state.wire.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nothing yet — this journey starts with a screen the respondent was sent to
                  rather than a request they made.
                </p>
              ) : (
                <ul className="grid gap-2">
                  {state.wire.map((call, index) => (
                    <WireEntry key={`${call.path}-${index}`} call={call} index={index} />
                  ))}
                </ul>
              )}
            </aside>
          </div>
        )}

        {view === 'map' && <FlowMap state={state} />}
        {view === 'staff' && <StaffQueuePanel />}
        {view === 'gaps' && <GapsPanel />}
      </div>
    </StudyChrome>
  );
}

function RunFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono text-[var(--color-royal-blue)]">{value}</dd>
    </div>
  );
}
